// ──────────────────────────────────────────────────────────────────────────
// WAITLIST API CLIENT
//
// Thin fetch wrapper around the Giga waitlist backend. The frontend calls
// these two endpoints; everything durable (queue positions, dedupe by email,
// referral attribution, emails) is the server's responsibility.
//
//   POST  {BASE}/join          { email, referredBy?, keepPosted?, source? }
//                              -> WaitlistJoinResult
//   GET   {BASE}/status/:code  -> WaitlistStatusResult
//
// See docs/waitlist-api-spec.md for the full contract handed to the backend.
//
// IMPORTANT: until the backend is live, requests will fail (404 / network).
// Callers MUST treat a thrown error as "API not available yet" and fall back
// to a local/demo experience — never block the user on a missing backend.
// ──────────────────────────────────────────────────────────────────────────

// Single source of truth for the API host. Swap this when the endpoint moves.
export const WAITLIST_API_BASE = 'https://app.getgiga.com/api/waitlist';

// Which success state the server resolved this submission to:
//   'new'       — first time we've seen this email (celebrate).
//   'returning' — email already on the list (welcome back, show drift).
//   'referred'  — someone joined via this person's link (bump celebration).
export type WaitlistStatus = 'new' | 'returning' | 'referred';
export type WaitlistJoinResult = {
  status: WaitlistStatus;
  // Current 1-based position in the queue.
  position: number;
  // Position BEFORE the most recent improvement (referral bump or hourly
  // drift), so the UI can animate the climb. Omit when there's no movement.
  previousPosition?: number;
  // How many friends have joined through this person's referral link so far.
  referralCount: number;
  // This person's own referral code (stable across submissions for an email).
  code: string;
  // Optional fully-qualified invite link. When omitted the FE builds /r/<code>.
  referralLink?: string;
  // Echoed back, normalized (lowercased/trimmed) by the server.
  email: string;
};
export type WaitlistStatusResult = {
  position: number;
  previousPosition?: number;
  referralCount: number;
  code: string;
  referralLink?: string;
};
export type JoinWaitlistInput = {
  email: string;
  // Referral code of whoever invited this visitor, if any.
  referredBy?: string | null;
  // Whether the visitor opted into product-news emails.
  keepPosted?: boolean;
  // Free-form attribution for where the signup originated (e.g. 'marketing-site').
  source?: string;
};

// Network timeout so a hanging/missing backend doesn't leave the button
// spinning forever — we bail and let the caller fall back to the demo state.
const REQUEST_TIMEOUT_MS = 8000;
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${WAITLIST_API_BASE}${path}`, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      signal: controller.signal,
      ...init
    });
    if (!res.ok) {
      // Surface a typed-ish error so callers can branch on validation vs. outage.
      let message = `Waitlist API responded ${res.status}`;
      try {
        const body = await res.json();
        if (body?.error?.message) message = body.error.message;
      } catch {

        /* non-JSON error body — keep the generic message */}
      const err = new Error(message) as Error & {
        status?: number;
      };
      err.status = res.status;
      throw err;
    }
    return (await res.json()) as T;
  } finally {
    clearTimeout(timer);
  }
}

// Join (or re-join) the waitlist. Resolves with the server-decided state, or
// throws if the API is unreachable / errors — callers fall back to demo.
export function joinWaitlist(input: JoinWaitlistInput): Promise<WaitlistJoinResult> {
  return request<WaitlistJoinResult>('/join', {
    method: 'POST',
    body: JSON.stringify({
      email: input.email.trim(),
      referredBy: input.referredBy ?? null,
      keepPosted: input.keepPosted ?? true,
      source: input.source ?? 'marketing-site'
    })
  });
}

// Re-fetch a known visitor's current queue position (e.g. on a return visit by
// referral code). Optional — the modal works without it.
export function getWaitlistStatus(code: string): Promise<WaitlistStatusResult> {
  return request<WaitlistStatusResult>(`/status/${encodeURIComponent(code)}`);
}